import { Helmet } from "react-helmet";
import Header from "../components/Header";
import SideNav from "../components/SideNav";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import {
  fetchReferralAnalytics,
  fetchReferralLeaderboard,
  fetchStaffLeaderboard,
  fetchReferralStates,
  fetchAllReferrals,
  fetchReferralTypes,
  fetchMilestones,
  fetchReferralCycles,
  fetchRewardWithdrawals,
  fetchMilestoneLogs,
  fetchPayoutTiers,
  fetchLeaderboardWinners,
} from "../actions/referralActions";
import useHasPermission from "../hooks/useHasPermission";
import { PERMISSIONS } from "../constants/permissions";

import AllReferrals from "./referral/AllReferrals";
import CustomerLeaderboard from "./referral/CustomerLeaderboard";
import StaffLeaderboard from "./referral/StaffLeaderboard";
import Milestones from "./referral/Milestones";
import RewardCycles from "./referral/RewardCycles";
import RewardWithdrawals from "./referral/RewardWithdrawals";
import MilestoneLogs from "./referral/MilestoneLogs";
import PayoutTiers from "./referral/PayoutTiers";
import LeaderboardWinners from "./referral/LeaderboardWinners";

const STATE_COLORS = {
  1: "#ffc107",
  2: "#28a745",
  3: "#007bff",
  4: "#6f42c1",
  5: "#dc3545",
};

const Referral = () => {
  const dispatch = useDispatch();
  const initRef = useRef(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);

  const canViewReferralRewards = useHasPermission(PERMISSIONS.VIEW_REFERRAL_REWARDS);
  const canManageMilestones = useHasPermission(PERMISSIONS.MANAGE_REFERRAL_MILESTONES);
  const canManageTiers = useHasPermission(PERMISSIONS.MANAGE_LEADERBOARD_TIERS);

  const { loading: analyticsLoading, data: analyticsData, error: analyticsError } =
    useSelector((state) => state.getReferralAnalytics) || {};
  const { data: statesData } = useSelector((state) => state.getReferralStates) || {};
  const { data: typesData } = useSelector((state) => state.getReferralTypes) || {};

  const analytics = analyticsData?.data;
  const stateLabels = (statesData?.data?.states || []).reduce((acc, s) => {
    acc[s.id] = { label: s.name, color: STATE_COLORS[s.id] || "#888" };
    return acc;
  }, {});
  const typeLabels = (typesData?.data?.types || []).reduce((acc, t) => {
    acc[t.id] = t.name;
    return acc;
  }, {});
  const typeOptions = typesData?.data?.types || [];

  useEffect(() => {
    if (initRef.current) {
      dispatch(fetchReferralAnalytics());
      dispatch(fetchAllReferrals(1, 100));
      dispatch(fetchReferralLeaderboard(1, 100));
      dispatch(fetchStaffLeaderboard(1, 100));
      dispatch(fetchReferralStates());
      dispatch(fetchReferralTypes());
      initRef.current = false;
    }
  }, [dispatch]);

  // Lazy-load tab data on first visit
  useEffect(() => {
    if (activeTab === "milestones") dispatch(fetchMilestones(false));
    if (activeTab === "cycles") dispatch(fetchReferralCycles(1, 100));
    if (activeTab === "withdrawals") dispatch(fetchRewardWithdrawals(1, 100));
    if (activeTab === "milestoneLogs") dispatch(fetchMilestoneLogs(1, 100));
    if (activeTab === "payoutTiers") dispatch(fetchPayoutTiers(false));
    if (activeTab === "winners") dispatch(fetchLeaderboardWinners(1, 100));
  }, [activeTab, dispatch]);

  const handleDateFilter = () => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("'From' date cannot be after 'To' date");
      return;
    }
    dispatch(fetchReferralAnalytics(fromDate, toDate));
    dispatch(fetchAllReferrals(1, 100, fromDate, toDate));
    dispatch(fetchReferralLeaderboard(1, 100, fromDate, toDate));
    dispatch(fetchStaffLeaderboard(1, 100, fromDate, toDate));
  };

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
    setActiveQuickFilter(null);
    dispatch(fetchReferralAnalytics());
    dispatch(fetchAllReferrals(1, 100));
    dispatch(fetchReferralLeaderboard(1, 100));
    dispatch(fetchStaffLeaderboard(1, 100));
  };

  const fmt = (d) => d.toISOString().slice(0, 10);
  const quickFilters = (() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(today.getMonth() - 1);
    const yearAgo = new Date(today);
    yearAgo.setFullYear(today.getFullYear() - 1);
    return [
      { label: "Yesterday", from: fmt(yesterday), to: fmt(yesterday) },
      { label: "Past Week", from: fmt(weekAgo), to: fmt(today) },
      { label: "Past Month", from: fmt(monthAgo), to: fmt(today) },
      { label: "Past Year", from: fmt(yearAgo), to: fmt(today) },
    ];
  })();

  const applyQuickFilter = (label, from, to) => {
    setFromDate(from);
    setToDate(to);
    setActiveQuickFilter(label);
    dispatch(fetchReferralAnalytics(from, to));
    dispatch(fetchAllReferrals(1, 100, from, to));
    dispatch(fetchReferralLeaderboard(1, 100, from, to));
    dispatch(fetchStaffLeaderboard(1, 100, from, to));
  };

  const tabs = [
    { key: "overview", label: "All Referrals", show: true },
    { key: "leaderboard", label: "Customer Leaderboard", show: true },
    { key: "staff", label: "Staff Leaderboard", show: true },
    { key: "milestones", label: "Milestones", show: canViewReferralRewards || canManageMilestones },
    { key: "cycles", label: "Reward Cycles", show: canViewReferralRewards },
    { key: "withdrawals", label: "Reward Withdrawals", show: canViewReferralRewards },
    { key: "milestoneLogs", label: "Milestone Logs", show: canViewReferralRewards },
    { key: "payoutTiers", label: "Payout Tiers", show: canViewReferralRewards || canManageTiers },
    { key: "winners", label: "Leaderboard Winners", show: canViewReferralRewards },
  ].filter((t) => t.show);

  return (
    <>
      <SideNav />
      <Helmet>
        <title>Admin | PennyTree - Referrals</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Referral Management</h1>

            {/* Date Range Filter */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: "13px", marginBottom: "4px", display: "block" }}>From:</label>
                <input
                  type="date"
                  className="invest-input"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setActiveQuickFilter(null); }}
                  style={{ width: "180px", height: "42px" }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label style={{ fontSize: "13px", marginBottom: "4px", display: "block" }}>To:</label>
                <input
                  type="date"
                  className="invest-input"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setActiveQuickFilter(null); }}
                  style={{ width: "180px", height: "42px" }}
                  min={fromDate || undefined}
                />
              </div>
              <div style={{ margin: 0 }}>
                <label style={{ fontSize: "13px", marginBottom: "4px", display: "block", visibility: "hidden" }}>.</label>
                <button className="login-btn" onClick={handleDateFilter} style={{ padding: "0 20px", width: "100px", height: "42px" }}>
                  Apply
                </button>
              </div>
              {(fromDate || toDate) && (
                <div style={{ margin: 0 }}>
                  <label style={{ fontSize: "13px", marginBottom: "4px", display: "block", visibility: "hidden" }}>.</label>
                  <button onClick={handleClearDates} style={{ padding: "0 16px", height: "42px", background: "transparent", border: "1px solid #666", borderRadius: "4px", cursor: "pointer", color: "#fff", fontSize: "14px" }}>
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Quick Date Filters */}
            <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
              {quickFilters.map((qf) => (
                <button
                  key={qf.label}
                  onClick={() => applyQuickFilter(qf.label, qf.from, qf.to)}
                  style={{
                    padding: "6px 14px",
                    border: `1px solid ${activeQuickFilter === qf.label ? "#007bff" : "#555"}`,
                    borderRadius: "20px",
                    background: activeQuickFilter === qf.label ? "#007bff" : "transparent",
                    color: activeQuickFilter === qf.label ? "#fff" : "#aaa",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: activeQuickFilter === qf.label ? "600" : "400",
                    transition: "all 0.15s",
                  }}
                >
                  {qf.label}
                </button>
              ))}
            </div>

            {/* Analytics Cards */}
            {analyticsLoading ? (
              <Loader />
            ) : analyticsError ? (
              <p className="text-danger text-center">{analyticsError}</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "30px" }}>
                {[
                  { label: "Total Referrals", value: analytics?.total_referrals, color: "#007bff" },
                  { label: "Successful", value: analytics?.successful_referrals, color: "#28a745" },
                  { label: "Pending", value: analytics?.pending_referrals, color: "#ffc107" },
                  { label: "Unique Referrers", value: analytics?.unique_referrers, color: "#6f42c1" },
                ].map((card) => (
                  <div key={card.label} style={{ background: "#1a1a1a", borderRadius: "8px", padding: "20px", borderLeft: `4px solid ${card.color}`, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                    <p style={{ fontSize: "13px", color: "#aaa", margin: 0 }}>{card.label}</p>
                    <h2 style={{ margin: "6px 0 0", fontSize: "28px", color: card.color }}>{card.value ?? "-"}</h2>
                  </div>
                ))}
              </div>
            )}

            {/* Tabs */}
            <div style={{ display: "flex", gap: "4px", marginBottom: "20px", borderBottom: "2px solid #333", flexWrap: "wrap" }}>
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: "10px 20px",
                    border: "none",
                    background: activeTab === tab.key ? "#007bff" : "transparent",
                    color: activeTab === tab.key ? "#fff" : "#aaa",
                    borderRadius: "4px 4px 0 0",
                    cursor: "pointer",
                    fontWeight: activeTab === tab.key ? "600" : "400",
                    fontSize: "14px",
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
              <AllReferrals
                stateLabels={stateLabels}
                typeLabels={typeLabels}
                typeOptions={typeOptions}
                fromDate={fromDate}
                toDate={toDate}
              />
            )}
            {activeTab === "leaderboard" && (
              <CustomerLeaderboard fromDate={fromDate} toDate={toDate} />
            )}
            {activeTab === "staff" && (
              <StaffLeaderboard stateLabels={stateLabels} fromDate={fromDate} toDate={toDate} />
            )}
            {activeTab === "milestones" && <Milestones />}
            {activeTab === "cycles" && <RewardCycles fromDate={fromDate} toDate={toDate} />}
            {activeTab === "withdrawals" && <RewardWithdrawals />}
            {activeTab === "milestoneLogs" && <MilestoneLogs />}
            {activeTab === "payoutTiers" && <PayoutTiers />}
            {activeTab === "winners" && <LeaderboardWinners />}
          </div>
        </div>
      </section>
    </>
  );
};

export default Referral;
